import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AuthInlineForm } from "@/components/auth/AuthInlineForm";
import { SEO } from "@/components/seo/SEO";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Stethoscope, Clock, Star } from "lucide-react";

const Auth = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!loading && user) {
      const returnTo = searchParams.get("returnTo");
      navigate(returnTo || '/dashboard');
    }
  }, [user, loading, navigate, searchParams]);

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return null; // Will be redirected by useEffect
  }

  return (
    <>
      <SEO 
        title="Sign In - QuickMedicalReview"
        description="Sign in to access your medical study dashboard, track progress, and continue practicing with expert-quality MCQs."
        path="/auth"
      />
      
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <div className="w-full max-w-md space-y-6">
          {/* Welcome Header */}
          <div className="text-center space-y-4">
            <div className="h-12 w-12 rounded-lg bg-gradient-brand shadow-elevated mx-auto" />
            <div>
              <h1 className="text-2xl font-bold">Welcome Back</h1>
              <p className="text-muted-foreground">Sign in to continue your medical studies</p>
            </div>
          </div>

          {/* Auth Card */}
          <Card className="shadow-soft">
            <CardHeader>
              <CardTitle>Sign In or Create Account</CardTitle>
              <CardDescription>
                Get instant access to 2000+ medical MCQs and study materials
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AuthInlineForm onSuccess={() => navigate('/dashboard')} />
            </CardContent>
          </Card>

          {/* Benefits */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Badge variant="outline" className="w-8 h-8 rounded-full p-0 flex items-center justify-center">
                <Clock className="w-4 h-4" />
              </Badge>
              <span>Get instant 3-day free trial on signup</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Badge variant="outline" className="w-8 h-8 rounded-full p-0 flex items-center justify-center">
                <Stethoscope className="w-4 h-4" />
              </Badge>
              <span>Access expert-reviewed medical content</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Badge variant="outline" className="w-8 h-8 rounded-full p-0 flex items-center justify-center">
                <Star className="w-4 h-4" />
              </Badge>
              <span>Track your progress and performance</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Auth;